import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../theme';
import { useLanguage } from '../lib/language';

// Text shadows for readability across dynamic backgrounds
const textShadow = '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)';
const textShadowLight = '0 1px 3px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)';

const Footer: React.FC = () => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const currentYear = new Date().getFullYear();
  const isLight = theme.mood === 'light';

  return (
    <footer
      className="relative z-20 w-full"
      style={{
        backgroundColor: isLight ? 'rgba(255, 255, 255, 0.92)' : 'rgba(0, 0, 0, 0.9)',
        borderTop: isLight ? '1px solid rgba(0,0,0,0.16)' : '1px solid rgba(255,255,255,0.14)',
      }}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-16">
        {/* Main footer content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-4 space-y-6">
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="relative w-8 h-8 flex items-center justify-center">
                <span
                  className="font-black text-lg"
                  style={{ color: 'var(--theme-text-primary)', textShadow }}
                >
                  M
                </span>
                <div
                  className="absolute inset-0"
                  style={{ border: '1px solid var(--theme-panel-border)' }}
                />
              </div>
              <span
                className="font-bold text-sm tracking-[0.1em] uppercase"
                style={{ color: 'var(--theme-text-primary)', textShadow }}
              >
                Musashi
              </span>
            </Link>
            <p
              className="text-sm leading-relaxed max-w-xs"
              style={{ color: isLight ? 'rgba(17,24,39,0.9)' : 'rgba(255,255,255,0.9)', textShadow: textShadowLight }}
            >
              {language === 'es'
                ? 'ERP, Productos AI, Páginas Web y Marketplace con estándar serio de ingeniería, diseño premium y soporte técnico.'
                : 'ERP, AI Products, Websites, and Marketplace with serious engineering, premium design, and technical support.'}
            </p>
          </div>

          {/* Navigation */}
          <div className="md:col-span-4">
            <p
              className="text-[10px] uppercase tracking-[0.2em] mb-6 font-semibold"
              style={{ color: isLight ? 'rgba(17,24,39,0.78)' : 'rgba(255,255,255,0.78)', textShadow: textShadowLight }}
            >
              {language === 'es' ? 'Navegación' : 'Navigation'}
            </p>
            <nav className="space-y-3">
              {[
                { label: language === 'es' ? 'Proyectos' : 'Work', path: '/works' },
                { label: language === 'es' ? 'Estudio' : 'Studio', path: '/studio' },
                { label: language === 'es' ? 'Contacto' : 'Contact', path: '/contact' }
              ].map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="block text-sm transition-colors hover:opacity-80"
                  style={{ color: isLight ? 'rgba(17,24,39,0.92)' : 'rgba(255,255,255,0.9)', textShadow: textShadowLight }}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Social */}
          <div className="md:col-span-4">
            <p
              className="text-[10px] uppercase tracking-[0.2em] mb-6 font-semibold"
              style={{ color: isLight ? 'rgba(17,24,39,0.78)' : 'rgba(255,255,255,0.78)', textShadow: textShadowLight }}
            >
              {language === 'es' ? 'Líneas' : 'Lines'}
            </p>
            <div className="flex flex-wrap gap-4">
              {(language === 'es' ? ['ERP', 'Productos AI', 'Páginas Web', 'Marketplace'] : ['ERP', 'AI Products', 'Websites', 'Marketplace']).map((social) => (
                <a
                  key={social}
                  href="/#work"
                  className="text-sm transition-colors hover:opacity-80"
                  style={{ color: isLight ? 'rgba(17,24,39,0.92)' : 'rgba(255,255,255,0.9)', textShadow: textShadowLight }}
                >
                  {social}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="py-6 flex items-center justify-center"
          style={{ borderTop: isLight ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.12)' }}
        >
          <div
            className="flex items-center gap-6 text-[11px]"
            style={{ color: isLight ? 'rgba(17,24,39,0.8)' : 'rgba(255,255,255,0.82)', textShadow: textShadowLight }}
          >
            <span>&copy; {currentYear} Musashi Studio</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
